/**
 * html★ (htmlstar)
 * A platform-native hypermedia framework for modern browsers
 *
 * @version 0.5.0
 * @license MIT
 */

import { resolve, hasAttr, resolveAll, DEFAULTS } from './cascade.js';
import { getConfig, clearConfigCache } from './config.js';
import { swap, handleRequest, getURL, getMethod, shouldSkip, registerSwapStrategy, unregisterSwapStrategy } from './request.js';
import { initTriggers, debounce } from './triggers.js';
import { initPersistence, clearPersisted, clearPersistedByPrefix } from './state.js';
import { initSSE, initPolling, closeSSE, closeAllSSE, stopPolling } from './sse.js';
import { initNavigation, updateHistory, hasNavigationAPI } from './navigation.js';
import { initPrefetch, prefetch } from './prefetch.js';
import { getFromCache, setInCache, invalidateCache, invalidateCacheByPattern, clearCache, getCacheStats, parseDuration, parseCacheConfig } from './cache.js';
import { hasViewTransitions, swapWithTransition, setTransitionName, clearTransitionName, injectDefaultStyles } from './transitions.js';

/**
 * Initialize htmlstar on a root element
 * Called automatically on DOMContentLoaded, and after swaps
 *
 * @param {HTMLElement|Document} root - Root element to initialize
 */
function init(root = document) {
  // Initialize all trigger types
  initTriggers(root, init);

  // Initialize persistence
  initPersistence(root);

  // Initialize SSE and polling
  initSSE(root);
  initPolling(root, init);

  // Initialize prefetch on hover
  initPrefetch(root);
}

/**
 * Auto-initialize when DOM is ready
 */
function autoInit() {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  // Initialize navigation handling (Navigation API or History API fallback)
  initNavigation(init);
}

// Auto-initialize
autoInit();

// Export public API
const htmlstar = {
  // Core
  init,
  resolve,
  hasAttr,
  resolveAll,
  swap,

  // Config
  getConfig,
  clearConfigCache,

  // Request helpers
  getURL,
  getMethod,
  shouldSkip,
  handleRequest,
  registerSwapStrategy,
  unregisterSwapStrategy,

  // Utilities
  debounce,

  // State
  clearPersisted,
  clearPersistedByPrefix,

  // SSE
  closeSSE,
  closeAllSSE,
  stopPolling,

  // Navigation
  hasNavigationAPI,
  updateHistory,

  // Prefetch
  prefetch,

  // Cache
  getFromCache,
  setInCache,
  invalidateCache,
  invalidateCacheByPattern,
  clearCache,
  getCacheStats,
  parseDuration,
  parseCacheConfig,

  // Transitions
  hasViewTransitions,
  swapWithTransition,
  setTransitionName,
  clearTransitionName,
  injectDefaultStyles,

  // Version
  version: '0.5.0',

  // Defaults (read-only reference)
  DEFAULTS: Object.freeze({ ...DEFAULTS })
};

// Expose globally for non-module usage
if (typeof window !== 'undefined') {
  window.htmlstar = htmlstar;
}

export default htmlstar;
export {
  init,
  resolve,
  hasAttr,
  resolveAll,
  swap,
  getConfig,
  clearConfigCache,
  getURL,
  getMethod,
  shouldSkip,
  handleRequest,
  registerSwapStrategy,
  unregisterSwapStrategy,
  debounce,
  clearPersisted,
  clearPersistedByPrefix,
  closeSSE,
  closeAllSSE,
  stopPolling,
  hasNavigationAPI,
  updateHistory,
  prefetch,
  getFromCache,
  setInCache,
  invalidateCache,
  invalidateCacheByPattern,
  clearCache,
  getCacheStats,
  parseDuration,
  parseCacheConfig,
  hasViewTransitions,
  swapWithTransition,
  setTransitionName,
  clearTransitionName,
  injectDefaultStyles,
  DEFAULTS
};
